<template>
  <div class="travel-view overflow-hidden">
    <!-- Image de fond avec effet parallax -->
    <div v-if="!loading" class="parallax-background position-fixed top-0 start-0 w-100 vh-100" ref="parallaxBackground">
      <picture  class="parallax-image w-100 h-100 d-block">
        <source :srcset="parallaxImage" media="(max-width: 414px) and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 2), (max-width: 414px) and (orientation: portrait) and (min-device-pixel-ratio: 2), (max-width: 414px) and (orientation: portrait) and (min-resolution: 192dpi), (max-width: 414px) and (orientation: portrait) and (min-resolution: 2dppx)">
        <source :srcset="parallaxImage" media="(max-width: 414px) and (orientation: portrait)">
        <source :srcset="parallaxImage" media="(max-width: 768px) and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 2), (max-width: 768px) and (orientation: portrait) and (min-device-pixel-ratio: 2), (max-width: 768px) and (orientation: portrait) and (min-resolution: 192dpi), (max-width: 768px) and (orientation: portrait) and (min-resolution: 2dppx)">
        <source :srcset="parallaxImage" media="(max-width: 768px) and (orientation: portrait)">
        <source :srcset="parallaxImage" media="(max-width: 1024px)">
        <source :srcset="parallaxImage" media="(max-width: 1280px)">
        <source :srcset="parallaxImage" media="(max-width: 1440px)">
        <source :srcset="parallaxImage" media="(max-width: 1600px)">
        <source :srcset="parallaxImage" media="(min-width: 1601px)">
        <img class="w-100 h-100 object-fit-cover" :src="parallaxImage">
      </picture>
      <!-- <div v-else class="w-100 vh-100">
        <div class="bg-light parallax-image w-100 h-100 d-block bg-opacity-50"></div>
      </div> -->
    </div>

    <!-- Contenu principal -->
    <PageView :loading="loading" header-class="bg-opacity-25 bg-light" :logo="logo" class="bg-light" :clear-container-classes="true" :container-class="`flex-grow-1 px-4 z-1 ${alignLogoLeft ? 'container-fluid mx-0' : 'container-lg'}`">
      <div class="content-overlay" v-if="participation?.travel.id">
          <div class="hero-section d-flex vh-100 align-items-start justify-content-center">
            <div :class="`d-flex flex-column justify-content-between text-white p-2 ${alignLogoLeft ? 'text-start px-0 w-100 px-0' : 'text-center p-sm-3 p-md-4 p-lg-3 mx-1 mx-sm-2 mx-md-0'}`"
              style="height: calc(100% - 8rem) !important"
              >
              <div class="hero-content">
                <img v-if="participation.travel.titleBanner?.key" :src="getPictureUrl(participation.travel.titleBanner.key)" style="max-height: 20vh;max-width: 60vw;" />
                <h1 v-else class="hero-title m-0 fw-bold fs-2 fs-md-1">{{ participation.travel.name }}</h1>
              </div>
              <div class="d-flex flex-column align-self-center mb-5 mb-md-0">
                <router-link :to="{ name: 'travel-registration-slug', params: { slug: participation.travel.slug } }" class="btn text-primary border-primary bg-white btn-lg mb-3 mb-md-2 fs-4 bg-opacity-75 d-flex align-items-center" style="height: 5rem;">
                    <span class="mx-sm-5 text-nowrap text-uppercase">Mon inscription</span> <i class="fa-solid fa-play-circle ps-2"></i>
                </router-link>
                <router-link v-if="participation.travel.canRefuse" :to="{ name: 'travel-optout-slug', params: { slug: participation.travel.slug } }" class="btn btn-outline-primary text-primary bg-white bg-opacity-75 btn-sm mb-2 text-uppercase">
                    {{ participation.travel.refusalLabel ?? 'Je ne participe pas au séjour' }}
                </router-link>
              </div>
            </div>
          </div>
      </div>
    </PageView>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import { getPictureUrl } from '@/compositions/helper';
import travelApi from '@/api/travelApi'
import PageView from '@/views/PageView.vue';
import { useRouter } from 'vue-router';
import { useNotificationsStore } from '@/stores/notificationStore';

const router = useRouter();
const notificationsStore = useNotificationsStore();

const loading = ref(true)
const props = defineProps<{id?: number, slug?: string}>()
const participation = ref<UserParticipation|null>(null);
const parallaxBackground = useTemplateRef<HTMLElement>('parallaxBackground');

let rafId: number | null = null;
let ticking = false;

const alignLogoLeft = computed(() => {
  return participation.value?.travel?.titleBannerAlignment === TravelDbTitleBannerAlignmentEnum.Left;
});

const logo = computed(() => {
  if (participation.value?.travel.logo?.key) {
    return getPictureUrl(participation.value.travel.logo.key);
  }

  return undefined;
});

const parallaxImage = computed(() => {
  if (participation.value?.travel.mainPicture?.key) {
    return getPictureUrl(participation.value.travel.mainPicture.key);
  } else {
    return ''
    // return new URL(`/src/assets/placeholder_travel.png`, import.meta.url).href;
  }
});

const updateParallax = () => {
  if (!parallaxBackground.value) return;

  const scrolled = window.pageYOffset;
  const parallaxFactor = 0.5; // Vitesse de l'effet parallax (0.5 = moitié de la vitesse normale)
  const yPos = -(scrolled * parallaxFactor);

  // Utiliser transform3d pour l'accélération matérielle
  parallaxBackground.value.style.transform = `translate3d(0, ${yPos}px, 0)`;

  ticking = false;
};

const onScroll = () => {
  if (!ticking) {
    rafId = requestAnimationFrame(updateParallax);
    ticking = true;
  }
};

// Fonction pour détecter si l'utilisateur préfère un mouvement réduit
const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

onMounted(() => {
  if (props.id) {
    travelApi.getParticipation(Number(props.id)).then((response) => {
      participation.value = response.data;
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors du chargement de la page',error.message);
      router.push({ name: 'home' });
    }).finally(() => {
      loading.value = false;
    });
  } else if (props.slug) {
    travelApi.getParticipationBySlug(props.slug).then((response) => {
      participation.value = response.data;
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors du chargement de la page',error.message);
      router.push({ name: 'home' });
    }).finally(() => {
      loading.value = false;
    });
  }

  // Vérifier si l'utilisateur préfère un mouvement réduit
  if (!prefersReducedMotion()) {
    // Ajouter l'écouteur de scroll avec des options optimisées
    window.addEventListener('scroll', onScroll, { passive: true });

    // Initialiser la position
    updateParallax();
  }
});

onUnmounted(() => {
  // Nettoyer les écouteurs et animations
  window.removeEventListener('scroll', onScroll);
  if (rafId) {
    cancelAnimationFrame(rafId);
  }
});

</script>

<style scoped>
.parallax-background {
  height: 120vh; /* Plus grand que viewport pour l'effet parallax */
  z-index: 0;
  will-change: transform;
  transform: translate3d(0, 0, 0); /* Force l'accélération matérielle */
}

.parallax-image img {
  object-position: center;
}

.hero-title {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  line-height: 1.2;
}

/* Support pour prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .parallax-background {
    position: static !important;
    height: 50vh !important;
    transform: none !important;
  }

  .hero-section {
    height: 50vh !important;
  }
}

/* Responsive pour les petits écrans */
@media (max-width: 768px) {
  .parallax-background {
    height: 110vh; /* Réduire légèrement sur mobile */
  }
}

/* Optimisations pour les écrans haute résolution */
@media (min-resolution: 2dppx) {
  .parallax-image img {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}
</style>
